import { createFileRoute } from "@tanstack/react-router";
import { useUser, useSession } from "@descope/react-sdk";
import { CheckCircle, Activity, Users, TrendingUp } from "lucide-react";
import { useAllCellTenants } from "@/hooks/use-cell-tenants";
import { useAllCellHealth } from "@/hooks/use-cell-health";
import { CellFailuresAlert } from "@/components/cells/cell-failures-alert";
import { healthUnknown } from "@/lib/cells/cell-health";
import { CELLS } from "@/lib/cells/cells";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TenantCreationChart } from "@/components/charts/tenant-creation-chart";
import { TenantGrowthChart } from "@/components/charts/tenant-growth-chart";

export const Route = createFileRoute("/")({
  component: Dashboard,
});

function Dashboard() {
  const { user, isUserLoading } = useUser();
  const { isAuthenticated } = useSession();
  const { tenants, failures: tenantFailures } = useAllCellTenants();
  const { primary, allHealthy, failures: healthFailures } = useAllCellHealth({
    readiness: false,
  });
  const health = primary?.health;
  // One notice per failing cell, and none with a single cell.
  const shownTenantFailures = CELLS.length > 1 ? tenantFailures : [];
  const shownHealthFailures = shownTenantFailures.length
    ? healthFailures.filter(
        ({ cell }) => !shownTenantFailures.some((f) => f.cell.id === cell.id),
      )
    : CELLS.length > 1
      ? healthFailures
      : [];

  if (isUserLoading || !isAuthenticated) {
    return (
      <div className="flex h-full min-w-0 flex-1 items-center justify-center">
        <div className="border-primary h-12 w-12 animate-spin rounded-full border-b-2"></div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-w-0 flex-1 flex-col gap-6 overflow-hidden">
      {/* Header */}
      <div className="shrink-0 flex flex-row items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold tracking-[-1%]">Overview</h2>
          <p className="text-muted-foreground">
            Welcome back, {user.name ?? "User"}. Here's what's happening with
            your tenant management.
          </p>
        </div>
        <Badge variant="secondary" className="text-sm">
          Live
        </Badge>
      </div>

      <div className="flex flex-1 flex-col gap-6 overflow-y-auto">
        <CellFailuresAlert failures={shownTenantFailures} />
        <CellFailuresAlert
          failures={shownHealthFailures}
          heading={healthUnknown}
        />

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tenants</CardTitle>
            <Users className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tenants.length}</div>
            <p className="text-muted-foreground text-xs">Configured tenants</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Service Status
            </CardTitle>
            <Activity className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {allHealthy ? "Healthy" : "Unknown"}
            </div>
            <p className="text-muted-foreground text-xs">Core services</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Uptime</CardTitle>
            <TrendingUp className="text-muted-foreground h-4 w-4" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {health?.uptime ? `${Math.floor(health.uptime / 60)}m` : "N/A"}
            </div>
            <p className="text-muted-foreground text-xs">Service uptime</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">System Status</CardTitle>
            <CheckCircle
              className={`h-4 w-4 ${allHealthy ? "text-green-500" : "text-gray-400"}`}
            />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {allHealthy ? "Online" : "Unknown"}
            </div>
            <p className="text-muted-foreground text-xs">
              Overall system status
            </p>
          </CardContent>
        </Card>
      </div>

        {/* Tenant Analytics */}
        <div className="grid gap-6 lg:grid-cols-2">
          <TenantCreationChart tenants={tenants} />
          <TenantGrowthChart tenants={tenants} />
        </div>
      </div>
    </div>
  );
}
